// Copyright (c) 2026 Vigeos SASU. Tous droits réservés.
import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControl, IconButton, InputLabel, MenuItem, Select, TextField, Tooltip, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import DeleteIcon from '@mui/icons-material/Delete';
import api from '../../utils/api';
import { dateTime } from '../../utils/dates';
import { errorMessage } from '../../utils/erreurs';

const ROLES = {
  tous: 'Tous les crans', soignant: 'Soignants', cadre: 'Cadres', direction: 'Direction',
};

/**
 * Numéros de repli SMS d'un établissement, seul filet de nuit quand aucun
 * téléphone n'accepte de notification : l'alerte part alors par SMS vers les
 * numéros du site, et d'aucun autre. La liste s'enregistre entière, dans
 * l'ordre d'envoi. Le serveur valide seul (format, doublons, nombre maximal)
 * et une ligne fautive refuse tout le lot.
 */
export default function SmsFallbackDialog({ site, onFermer: onClose, onToast }) {
  const [state, setState] = useState({ chargement: true, erreur: '', vue: null });
  const [rows, setLines] = useState([]);
  const [enCours, setEnCours] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!site) return undefined;
    let alive = true;
    setState({ chargement: true, erreur: '', vue: null });
    setError('');
    api.get(`/pro/admin/sites/${site.id}/sms-contacts`)
      .then((r) => {
        if (!alive) return;
        setState({ chargement: false, erreur: '', vue: r.data });
        setLines((r.data.contacts || []).map((c) => ({ label: c.label || '', number: c.number || '', role: c.role || 'tous' })));
      })
      .catch((e) => { if (alive) setState({ chargement: false, erreur: errorMessage(e, 'Numéros illisibles'), vue: null }); });
    return () => { alive = false; };
  }, [site]);

  const updateRow = (i, patch) => setLines((ls) => ls.map((l, j) => (j === i ? { ...l, ...patch } : l)));
  const move = (i, direction) => setLines((ls) => {
    const j = i + direction;
    if (j < 0 || j >= ls.length) return ls;
    const copy = [...ls];
    [copy[i], copy[j]] = [copy[j], copy[i]];
    return copy;
  });

  const save = async () => {
    setEnCours(true); setError('');
    try {
      const r = await api.put(`/pro/admin/sites/${site.id}/sms-contacts`, {
        contacts: rows.map((l) => ({ label: l.label, number: l.number, role: l.role })),
      });
      setState({ chargement: false, erreur: '', vue: r.data });
      onToast?.(`Numéros de repli de ${site.name} enregistrés`);
      onClose();
    } catch (e) {
      setError(errorMessage(e, 'Numéros refusés'));
    } finally {
      setEnCours(false);
    }
  };

  const view = state.vue;
  const ready = view?.readiness;
  const max = view?.max_contacts;

  return (
    <Dialog open={!!site} onClose={() => !enCours && onClose()} maxWidth="md" fullWidth>
      <DialogTitle>Numéros de repli SMS : {site?.name}</DialogTitle>
      <DialogContent dividers>
        {state.chargement ? (
          <CircularProgress size={20} />
        ) : state.erreur ? (
          <Alert severity="error">{state.erreur}</Alert>
        ) : (
          <>
            {ready && (
              <Alert severity={ready.night_ready ? 'success' : (ready.numbers_ok ? 'warning' : 'error')} sx={{ mb: 2 }}>
                {ready.reason}
                {ready.levels_uncovered?.length > 0 && ready.numbers_ok
                  ? ` Crans non couverts : ${ready.levels_uncovered.join(', ')}.` : ''}
              </Alert>
            )}
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Dans l&apos;ordre d&apos;envoi{max ? `, ${max} numéros au plus` : ''}. Saisie tracée au
              journal d&apos;administration.
              {view?.updated_at ? ` Dernière modification le ${dateTime(view.updated_at, 'jamais')}${view.updated_by ? ` par ${view.updated_by}` : ''}.` : ''}
            </Typography>
            {rows.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Aucun numéro : enregistrer une liste vide retire tous les numéros.
              </Typography>
            )}
            {rows.map((l, i) => (
              <Box key={i} sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1, flexWrap: 'wrap' }}>
                <Typography variant="caption" sx={{ width: 16 }}>{i + 1}</Typography>
                <TextField size="small" label="Libellé" sx={{ flex: 2, minWidth: 180 }}
                  value={l.label} onChange={(e) => updateRow(i, { label: e.target.value })}
                  placeholder="Poste de soins de nuit" disabled={enCours} />
                <TextField size="small" label="Numéro" sx={{ flex: 1, minWidth: 150 }}
                  value={l.number} onChange={(e) => updateRow(i, { number: e.target.value })}
                  placeholder="06 12 34 56 78" disabled={enCours} />
                <FormControl size="small" sx={{ minWidth: 140 }} disabled={enCours}>
                  <InputLabel>Rôle</InputLabel>
                  <Select label="Rôle" value={l.role} onChange={(e) => updateRow(i, { role: e.target.value })}>
                    {(view?.roles || Object.keys(ROLES)).map((r) => (
                      <MenuItem key={r} value={r}>{ROLES[r] || r}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Tooltip title="Monter"><span>
                  <IconButton size="small" onClick={() => move(i, -1)} disabled={enCours || i === 0}>
                    <ArrowUpwardIcon fontSize="small" />
                  </IconButton>
                </span></Tooltip>
                <Tooltip title="Descendre"><span>
                  <IconButton size="small" onClick={() => move(i, 1)} disabled={enCours || i === rows.length - 1}>
                    <ArrowDownwardIcon fontSize="small" />
                  </IconButton>
                </span></Tooltip>
                <Tooltip title="Retirer"><span>
                  <IconButton size="small" onClick={() => setLines((ls) => ls.filter((_, j) => j !== i))} disabled={enCours}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </span></Tooltip>
              </Box>
            ))}
            <Button size="small" startIcon={<AddIcon />} disabled={enCours}
              onClick={() => setLines((ls) => [...ls, { label: '', number: '', role: 'tous' }])}>
              Ajouter un numéro
            </Button>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={enCours}>Annuler</Button>
        <Button variant="contained" onClick={save} disabled={enCours || state.chargement || !!state.erreur}>
          {enCours ? <CircularProgress size={18} /> : 'Enregistrer la liste'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
